import type { MouseEvent, ReactNode } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextLink from 'LuxKit/TextLink'
import useListTruncator from 'hooks/useListTruncator'
import CruiseBookingConditionsInfo from './CruiseBookingConditionsInfo'
import CruiseOfferInclusionItem from './CruiseOfferInclusionItem'

const DEFAULT_VISIBLE_ITEMS = 3

const InclusionItemWrapper = styled.div`
  min-width: 0;
`

const ToggleText = styled(TextLink)`
  white-space: nowrap;
`

interface Props {
  inclusions: Array<App.PackageInclusion>
  showMoreOnSeparateLine?: boolean
  visibleItems?: number
  endIcon?: ReactNode
}

function CruiseLimitedInclusionList({
  inclusions,
  showMoreOnSeparateLine = false,
  visibleItems = DEFAULT_VISIBLE_ITEMS,
  endIcon,
}: Props) {
  const intl = useIntl()
  const [showAll, setShowAll] = useState(false)
  const bookingConditions = useMemo(
    () => inclusions.filter((inclusion) => inclusion.displayType === 'BOOKING_CONDITION'),
    [inclusions],
  )
  const benefitInclusions = useMemo(
    () => inclusions.filter((inclusion) => inclusion.displayType !== 'BOOKING_CONDITION'),
    [inclusions],
  )
  const [visibleInclusions, hiddenInclusions] = useListTruncator(showAll ? undefined : visibleItems, benefitInclusions)
  const hiddenCount = hiddenInclusions.length
  const lastInclusionEndIcon = useMemo(() => {
    if (endIcon) return endIcon
    if (bookingConditions.length > 0) {
      return <CruiseBookingConditionsInfo conditions={bookingConditions} />
    }
  }, [bookingConditions, endIcon])

  const onShowMoreClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setShowAll(true)
  }, [])

  const onShowLessClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setShowAll(false)
  }, [])

  return <Group
    wrap="wrap"
    direction="vertical"
    gap={4}
    verticalAlign="center"
    horizontalAlign="start"
  >
    {visibleInclusions.map((inclusion, index) => {
      const shouldShowMoreToggle = !showMoreOnSeparateLine && !showAll && hiddenCount > 0 && index === visibleInclusions.length - 1
      const isLastVisibleInclusion = index === visibleInclusions.length - 1

      return <InclusionItemWrapper key={inclusion.id}>
        <CruiseOfferInclusionItem
          inclusion={inclusion}
          showMinStayDescription
          endIcon={isLastVisibleInclusion ? lastInclusionEndIcon : undefined}
          showMoreLink={shouldShowMoreToggle && <ToggleText
            size="medium"
            colour="normal-contrast"
            onClick={onShowMoreClick}
          >
            {intl.formatMessage({
              id: 'UtomIJF0H414JjvYQR_T',
              defaultMessage: '+{count} more',
              description: 'Action to expand and show more evergreen cruise inclusions',
            }, { count: hiddenCount })}
          </ToggleText>}
        />
      </InclusionItemWrapper>
    })}

    {!showAll && showMoreOnSeparateLine && hiddenCount > 0 && <ToggleText
      size="medium"
      colour="normal-contrast"
      onClick={onShowMoreClick}
    >
      {intl.formatMessage({
        id: 'UtomIJF0H414JjvYQR_T',
        defaultMessage: '+{count} more',
        description: 'Action to expand and show more evergreen cruise inclusions',
      }, { count: hiddenCount })}
    </ToggleText>}

    {showAll && benefitInclusions.length > visibleItems && <ToggleText size="medium" colour="normal-contrast" onClick={onShowLessClick}>
      {intl.formatMessage({
        id: 'H9GcFGylDblx4phqaAZa',
        defaultMessage: 'Show less',
        description: 'Action to collapse expanded evergreen cruise inclusions',
      })}
    </ToggleText>}

    {benefitInclusions.length === 0 && bookingConditions.length > 0 &&
      <CruiseBookingConditionsInfo conditions={bookingConditions} />}
  </Group>
}

export default CruiseLimitedInclusionList
